import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = 'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])';

/**
 * Constrains keyboard Tab navigation to elements inside `containerRef` while
 * `active` is true. Restores focus to the previously-focused element when
 * the trap deactivates / unmounts.
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, isOpen);
 *   <div ref={ref}>...</div>
 *
 * This is the standard modal a11y pattern (WCAG 2.4.3): focus must not
 * escape the modal via Tab, and Shift+Tab from the first element should
 * wrap to the last (and vice versa).
 */
export const useFocusTrap = (
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void => {
  useEffect(() => {
    if (!active) return undefined;
    const root = containerRef.current;
    if (!root) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('disabled'),
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      // Only act when focus is currently inside the trap. If focus has
      // escaped (e.g. backdrop click, programmatic focus from a portal),
      // the trap must not steal keyboard handling from the rest of the
      // page — the modal-close logic will reclaim focus on next render.
      if (!root.contains(document.activeElement)) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [active, containerRef]);
};
